import { GenericGridSmartViewsView } from '../view/GenericGridSmartViewsView';
import { templateGenericGridSmartViews } from '../view/template';
import { GenericGridSmartViewsRepository } from '../repository/GenericGridSmartViewsRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { GenericGridSmartViewsWebComponent } from '..';
import {
  GenericGridSmartViewsWebComponentSchema,
  SmartViewEvents,
  SmartViewItemConfiguration,
  SmartViewsCreateRequest,
  SmartViewsUpdateRequest,
  ValidateViewNameResponse,
  SmartViewState,
  SmartViewsRecentCreateRequest,
} from '../types';
import { SmartViewInlineActions } from '../../gridSmartViews/types';
import { ListItemEvents } from '../../../ui/gzListItem/types';
import { htmlEncode } from '../../../utils/escape.utils';
import { ButtonColors, ButtonTypes } from '../../../ui/gzButton/types';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { SaveViewFormDialogSchema } from '../../uiElements/smartViewSaveDialog/types';
import { GridSaveViewForm } from '../../uiElements/smartViewSaveDialog';
import { GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { RemoteValidator } from '../../../ui/gzForm/validators/remote.validator';
import { GzToast, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { translateFn } from '../../../utils/translator.helper';

export class GenericGridSmartViewsController extends BaseWebComponentController {
  public component: GenericGridSmartViewsWebComponent;
  public dataRepository: GenericGridSmartViewsRepository;
  public view: GenericGridSmartViewsView;
  public viewConstructor: typeof GenericGridSmartViewsView;
  public dataRepositoryConstructor: typeof GenericGridSmartViewsRepository;

  constructor(component: GenericGridSmartViewsWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: GenericGridSmartViewsView,
      viewTemplate: templateGenericGridSmartViews,
      dataRepositoryConstructor: GenericGridSmartViewsRepository,
    });
  }

  /**
   * will fetch smart views settings
   * in case smart views settings are not set(happens on the first load) we will upsert them
   * @returns {Promise<GenericGridSmartViewsWebComponentSchema>}
   */
  async getDataForView(): Promise<GenericGridSmartViewsWebComponentSchema> {
    this.view.setLoadingState();
    try {
      const smartViewSettings = await this.dataRepository.getSmartViewSettings(this.dependencies.componentName);
      const data = await this.dataRepository.getDataForView();
      return {
        categoriesConfig: this.component.categoriesConfig,
        data: data,
        settings: smartViewSettings as SmartViewState,
        hasSearchInput: this.component.hasSearchInput,
        searchPlaceholderString: this.component.searchPlaceholderString,
        emptySavedViewsText: this.component.emptySavedViewsText,
        initialView: this.component.initialView,
        customErrorMappingFn: this.component.customErrorMappingFn,
        searchFn: this.component.searchFn,
        decoratedTextOnSearchFn: this.component.decoratedTextOnSearchFn,
      };
    } catch (e) {
      throw e;
    } finally {
      this.view.removeLoadingState();
    }
  }

  public computeEventListeners() {
    return [
      {
        element: this.view.dom.$smartViewsLit,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.handleViewItemClicked,
      },
      {
        element: this.view.dom.$smartViewsLit,
        event: SmartViewEvents.SMART_VIEW_CATEGORY_OPEN,
        callback: this.handleCategoryOpened,
      },
      {
        element: this.view.dom.$smartViewsLit,
        event: SmartViewInlineActions.DELETE,
        callback: this.showDeleteDialog,
      },
      {
        element: this.view.dom.$smartViewsLit,
        event: ListItemEvents.SAVE,
        callback: this.handleViewNameUpdated,
      },
    ];
  }

  /**
   * creates a config for the save dialog
   */
  public saveSmartView(view: SmartViewsCreateRequest, viewCreationConfig) {
    const { smartViewsStrings } = this.view.getTranslations();
    const dialogText = viewCreationConfig.dialogText
      ? viewCreationConfig.dialogText
      : smartViewsStrings.saveViewFormDialog.dialogText;
    const config: SaveViewFormDialogSchema = {
      shouldDisplayCheckbox: false,
      dialogText: dialogText,
      nameFieldLabel: smartViewsStrings.saveViewFormDialog.nameFieldLabel,
      nameInputErrorText: smartViewsStrings.saveViewFormDialog.nameInputErrorText,
      nameInputPlaceholder: smartViewsStrings.saveViewFormDialog.nameInputPlaceholder,
      headline: smartViewsStrings.saveViewFormDialog.headline,
      saveButtonLabel: smartViewsStrings.actions.save,
      cancelButtonLabel: smartViewsStrings.actions.cancel,
      nameInputValidators: [
        RequiredValidator('This field is required.'),
        RemoteValidator(
          (value: string) => {
            const viewName = value.trimEnd().trimStart();

            return this.dependencies.api.clients.SmartViews.validateViewName(this.dependencies.componentName, {
              name: viewName,
            });
          },
          (response: ValidateViewNameResponse) => response.valid,
          (response: ValidateViewNameResponse) => this.view.translateValidateViewNameErrorCode(response.errorCode),
          this.dependencies.translateFn('A communication error has occurred. Please try again later.')
        ),
      ],
    };
    this.showSaveAsDialog(config, view);
  }

  public updateActiveViewAndItemAndTriggerSelectedEvent(event: WebComponentEvent): void {
    this.view.dom.$smartViewsLit.updateActiveItem(event.detail.smartViewConfiguration);
    this.view.activeView = event.detail.smartViewConfiguration;
    this.component.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_SELECTED, {
        detail: event.detail,
        bubbles: true,
      })
    );
  }

  public updateActiveViewById(viewId: string): void {
    const view: SmartViewItemConfiguration | null = this.view.dom.$smartViewsLit.getViewById(viewId);

    if (!view) {
      return;
    }

    this.view.dom.$smartViewsLit.updateActiveItem(view);
    this.view.activeView = view;
  }

  public updateActiveViewByLabel(viewLabel: string): void {
    const view: SmartViewItemConfiguration | null =
      this.view.dom.$smartViewsLit.getDefaultViewByOriginalLabel(viewLabel);
    if (!view) {
      return;
    }

    this.view.dom.$smartViewsLit.updateActiveItem(view);
    this.view.activeView = view;
  }

  /**
   * Will return a view by label or id. By default, it will return the view by label.
   * @param {string} filterValue
   * @param {boolean} getById
   * @returns {SmartViewItemConfiguration | null}
   */
  public getViewByLabelOrId(filterValue: string, getById: boolean = false): SmartViewItemConfiguration | null {
    return getById
      ? this.view.dom.$smartViewsLit.getViewById(filterValue)
      : this.view.dom.$smartViewsLit.getViewByLabel(filterValue);
  }

  /**
   * dispatches a smart view selected event with the clicked smart view configuration
   */
  private async handleViewItemClicked(event: WebComponentEvent): Promise<void> {
    if (typeof this.component.isEditedFn === 'function' && (await this.component.isEditedFn())) {
      this.showLeavingEditModeDialog(event);
      return;
    }
    this.updateActiveViewAndItemAndTriggerSelectedEvent(event);
  }

  private async handleCategoryOpened(event: WebComponentEvent) {
    const objIndex = this.view.dom.$smartViewsLit.categories.findIndex(
      category => category.sectionTitle.toLowerCase() === event.detail.sectionTitle.toLowerCase()
    );
    if (objIndex >= 0) {
      this.view.dom.$smartViewsLit.categories[objIndex].isOpen = event.detail.opened ? true : undefined;
    }
  }

  /**
   * updates a view name and dispatches a smart view name updated event
   * if the updated view is also the current loaded view we also need to make sure
   * the name is updated in the right component
   */
  async handleViewNameUpdated(event: WebComponentEvent) {
    const label = event.detail.value.trimLeft().trimRight();
    const updateReq = {
      id: event.detail.itemData._id,
      details: {
        label,
      },
    } as SmartViewsUpdateRequest;
    try {
      await this.dataRepository.updateCustomView(this.dependencies.componentName, updateReq);
    } catch (error) {
      let errorMessage = this.dependencies.translateFn('Failed to rename view.', { viewName: label });
      const mappedErrorMessage = this.view.translateValidateViewNameErrorCode(error.code);
      if (mappedErrorMessage) {
        errorMessage = mappedErrorMessage;
      }
      this.buildToastNotification(errorMessage, ToastSeverity.ERROR);
      await this.reloadCategories();
      return;
    }

    this.buildToastNotification(
      this.dependencies.translateFn('View name changed successfully.'),
      ToastSeverity.SUCCESS
    );
    this.component.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_NAME_UPDATED, {
        detail: {
          updatedViewName: event.detail.value.trimLeft().trimRight(),
          smartViewItemConfiguration: event.detail.itemData,
        },
        bubbles: true,
      })
    );
    await this.reloadCategories();
  }

  /**
   * updates a listItems data
   */
  private updateListItemData(itemData: SmartViewItemConfiguration) {
    this.view.updateListItemData(itemData);
  }

  /**
   * will show the delete dialog
   * on delete confirmation will make an API call to delete the view
   * in case the deleted view is actually the current active view we are going to make
   * an API call to update the settings
   * and we will dispatch an after delete event(if the current loaded view was delete we need to load
   * a new one)
   */
  async showDeleteDialog(event: WebComponentEvent) {
    const translations = this.view.getTranslations();
    const itemData = event.detail.itemData;
    const modalString = this.dependencies.translateFn('Are you sure you want to delete <strong>{viewName}</strong> ?', {
      viewName: htmlEncode(itemData.label),
    });

    const modalBodyString = `<p> ${modalString} </p>`;

    const footerString = `<gz-button type="${ButtonTypes.OUTLINE}" color="${
      ButtonColors.NEGATIVE
    }" id="confirm"> ${translations.smartViewsStrings.actions.delete.toUpperCase()} </gz-button>
                          <gz-button type="${ButtonTypes.OUTLINE}" color="${
      ButtonColors.SECONDARY
    }" id="cancel"> ${translations.smartViewsStrings.actions.cancel.toUpperCase()} </gz-button>
`;
    const cancelCallback = null;
    const confirmCallback = async () => {
      try {
        await this.dataRepository.deleteCustomView(this.dependencies.componentName, itemData._id);

        let focusOnSelectedView = false;

        if (this.view.smartViewState.selectedViewId === itemData._id) {
          this.view.smartViewState.selectedViewId = this.view.initialView?.view
            ? this.view.initialView?.view._id
            : null;
          await this.dataRepository.upsertState(this.dependencies.componentName, this.view.smartViewState);
          focusOnSelectedView = true;
        }
        this.view.initializeActiveViewIfNotPresent();
        await this.reloadCategories(focusOnSelectedView);
        this.buildToastNotification(
          this.dependencies.translateFn('Successfully deleted {viewName}', { viewName: itemData.label }),
          ToastSeverity.SUCCESS
        );
        this.component.dispatchEvent(
          new CustomEvent(SmartViewEvents.SMART_VIEW_AFTER_DELETE, {
            detail: {
              deletedView: itemData,
            },
            bubbles: true,
          })
        );
      } catch (err) {
        this.buildToastNotification(
          this.dependencies.translateFn('Failed to delete {viewName}', { viewName: itemData.label }),
          ToastSeverity.ERROR
        );
      }
    };

    GzDialogWebComponent.computeCustomDialog(
      modalBodyString,
      footerString,
      translations.smartViewsStrings.deleteViewFormDialog.title,
      cancelCallback,
      confirmCallback
    );
  }

  public showLeavingEditModeDialog(event: WebComponentEvent): void {
    const modalString = translateFn(
      'Exiting edit mode will discard any unsaved changes to this smart view.'
    );

    const modalBodyString = `<p> ${modalString} </p>`;

    const footerString = `
      <gz-button type="${ButtonTypes.OUTLINE}" color="${ButtonColors.NEGATIVE}" id="confirm">
        ${translateFn('Exit')}
      </gz-button>
      <gz-button type="${ButtonTypes.OUTLINE}" color="${ButtonColors.SECONDARY}" id="cancel">
        ${translateFn('Cancel')}
      </gz-button>
    `;

    const confirmCallback = async () => {
      this.updateActiveViewAndItemAndTriggerSelectedEvent(event);
    };

    GzDialogWebComponent.computeCustomDialog(
      modalBodyString,
      footerString,
      translateFn('Exit edit mode?'),
      null,
      confirmCallback
    );
  }

  private buildToastNotification(message: string, severity: ToastSeverity) {
    const toastNotificationConfigs: GzToast = {
      message,
      hasAction: false,
      severity: severity,
      keepOpen: false,
      autoCloseDelayMs: 10000,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
  }

  /**
   * shows the save as view dialog and adds event listeners on
   * confirm and cancel
   */
  public showSaveAsDialog(config: SaveViewFormDialogSchema, details: SmartViewsCreateRequest) {
    const saveForm = new GridSaveViewForm();

    if (config.shouldDisplayCheckbox) {
      saveForm.shouldDisplayCheckbox = config.shouldDisplayCheckbox;
      saveForm.checkboxLabel = config.checkboxLabel;
    }

    saveForm.saveButtonLabel = config.saveButtonLabel;
    saveForm.cancelButtonLabel = config.cancelButtonLabel;
    saveForm.dialogText = config.dialogText;
    saveForm.nameFieldLabel = config.nameFieldLabel;
    saveForm.nameInputErrorText = config.nameInputErrorText;
    saveForm.nameInputPlaceholder = config.nameInputPlaceholder;
    saveForm.nameInputValidators = config.nameInputValidators;
    saveForm.inputMaxLength = 64;

    const dialog = GzDialogWebComponent.buildDialogElement({
      headline: config.headline,
      bodyContent: saveForm,
      isOpen: true,
    });

    saveForm.addEventListener(GzDialogEvents.CANCEL, event => {
      dialog.close();
    });

    saveForm.addEventListener(GzDialogEvents.CONFIRM, async (event: WebComponentEvent) =>
      this.createNewViewFromDialog(event, details, dialog)
    );
  }

  private async createNewViewFromDialog(event: WebComponentEvent, details: SmartViewsCreateRequest, dialog) {
    details.label = event.detail.viewName;
    dialog.close();

    return this.createNewView(details);
  }

  /**
   * will persist the view and update the current active view in states
   * will trigger a rerender and dispatch an after view created event
   * after the creation of this view we need to also load the newly created view
   */
  public async createNewView(
    details: SmartViewsCreateRequest,
    forwardError: boolean = false
  ): Promise<SmartViewItemConfiguration | null> {
    try {
      const view = await this.dataRepository.addCustomView(this.dependencies.componentName, details);
      const settings = this.getViewState();
      settings.selectedViewId = view._id;
      // don't need to save the 'recent view' when saving a new custom view
      if (settings.savedViewConfiguration) {
        delete settings.savedViewConfiguration;
      }
      await this.dataRepository.upsertState(this.dependencies.componentName, settings);
      await this.reloadCategories(true);
      this.component.dispatchEvent(
        new CustomEvent(SmartViewEvents.SMART_VIEW_AFTER_CREATE, {
          detail: {
            createdView: view,
          },
          bubbles: true,
        })
      );
      this.buildToastNotification(
        this.dependencies.translateFn('Successfully created {viewName}', { viewName: details.label }),
        ToastSeverity.SUCCESS
      );
      return view;
    } catch (error) {
      if (forwardError) {
        throw error;
      }

      let errorMessage = this.dependencies.translateFn('Failed to create {viewName}', { viewName: details.label });
      const mappedErrorMessage = this.view.translateValidateViewNameErrorCode(error.code);

      if (mappedErrorMessage) {
        errorMessage = mappedErrorMessage;
      }

      this.buildToastNotification(errorMessage, ToastSeverity.ERROR);

      return null;
    }
  }

  /**
   * force renders the categories
   */
  public async reloadCategories(focusOnSelectedView: boolean = false): Promise<void> {
    this.component.propertiesSet = false;
    this.view.focusOnSelectedView = focusOnSelectedView;
    this.view.dom.$smartViewsLit.categories = [];
    this.component.propertiesSet = true;
  }

  /**
   * updates the current active view in states
   */
  public async updateActiveViewId(activeViewId: string) {
    this.view.smartViewState.selectedViewId = activeViewId;
    await this.dataRepository.upsertState(this.dependencies.componentName, { selectedViewId: activeViewId });
  }

  public setActiveViewAndSelect(viewId: string) {
    this.view.dom.$smartViewsLit.updateActiveAndSelectItem(viewId);
  }

  /**
   *
   * will return the current smart view settings
   */
  public async getSmartViewSettings(): Promise<SmartViewState> {
    return await this.dataRepository.getSmartViewSettings(this.dependencies.componentName);
  }

  /**
   * adds a smart view to favorites and force renders the component
   */
  public async makeSmartViewFavorite(viewId) {
    await this.dataRepository.addToFavorite(this.dependencies.componentName, viewId);
    await this.reloadCategories();
  }

  /**
   * removes a smart view from favorites and force renders the component
   */
  public async removeFromFavorite(viewId) {
    await this.dataRepository.removeFromFavorite(this.dependencies.componentName, viewId);
    await this.reloadCategories();
  }

  /**
   * updates a view and force renders the component
   */
  public async handleUpdateSmartView(view: SmartViewsUpdateRequest): Promise<SmartViewItemConfiguration | null> {
    let updatedView;
    try {
      updatedView = await this.dataRepository.updateCustomView(this.dependencies.componentName, view);
    } catch (error) {
      let errorMessage = this.dependencies.translateFn('Failed to update view');
      const mappedErrorMessage = this.view.translateValidateViewNameErrorCode(error.code);
      if (mappedErrorMessage) {
        errorMessage = mappedErrorMessage;
      }
      this.buildToastNotification(errorMessage, ToastSeverity.ERROR);

      return null;
    }

    //@ts-ignore
    if (this.view.activeView._id === updatedView._id) {
      this.view.activeView = updatedView;
    }
    this.updateListItemData(updatedView);

    return updatedView;
  }

  /**
   * updates a view and force renders the component
   */
  public async handleUpdateRecentSmartView(view: SmartViewsUpdateRequest) {
    const updatedView = await this.dataRepository.updateRecentView(this.dependencies.componentName, view.id, view);
    //@ts-ignore
    if (this.view.activeView._id === updatedView._id) {
      this.view.activeView = updatedView;
    }
    this.updateListItemData(updatedView);
  }

  /**
   *  will fetch the current active view
   */
  public getActiveView(): SmartViewItemConfiguration | undefined {
    return this.view.activeView;
  }

  public async setDefaultViewAsActiveView() {
    this.view.setDefaultViewAsActiveView();
    await this.dataRepository.upsertState(this.dependencies.componentName, this.view.smartViewState);
    await this.reloadCategories();
  }

  /**
   * will fetch the current smart view settings
   */
  public getViewState() {
    return this.view.smartViewState;
  }

  /**
   * will update the current smart view settings
   */
  public async updateSmartViewsSettings(settings) {
    return this.dataRepository.upsertState(this.dependencies.componentName, settings);
  }

  /**
   * will create a new view and add it in the recent category
   */
  public async addRecentView(details: SmartViewsRecentCreateRequest, select = false) {
    const view = await this.dataRepository.addRecentView(this.dependencies.componentName, details);
    if (select) {
      const settings = this.getViewState();
      settings.selectedViewId = view._id;
      this.view.activeView = view;
      await this.dataRepository.upsertState(this.dependencies.componentName, settings);
    }
    await this.reloadCategories(select);
    this.component.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_AFTER_RECENT_CREATE, {
        detail: {
          createdView: view,
        },
        bubbles: true,
      })
    );
    return view;
  }
}
